// src/views/admin/ADImportNotesExcel.jsx
import { useState, useEffect } from "react";
import { useAdminService } from "../../services/adminService";
import { Loader } from "../../components/Loader";
import { useToastHook } from "../../utils/useToast";
import * as XLSX from "xlsx";
import { useNavigate } from "react-router-dom";

function Modal({ title, onClose, children, maxWidth = 560 }) {
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-box" style={{ maxWidth }}>
        <div className="modal-title">
          {title}
          <button className="ibtn" style={{ border: "none" }} onClick={onClose}>
            <i className="ti ti-x" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Card({ children, title, icon }) {
  return (
    <div className="card" style={{ marginBottom: 16 }}>
      {title && (
        <div className="ch">
          <div className="ct">
            {icon && <i className={`ti ${icon}`} style={{ marginRight: 8 }} />}
            {title}
          </div>
        </div>
      )}
      {children}
    </div>
  );
}

export default function ADImportNotesExcel({ slug }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [plannifications, setPlannifications] = useState([]);
  const [selectedPlannification, setSelectedPlannification] = useState("");
  const [plannification, setPlannification] = useState(null);
  const [eleves, setEleves] = useState([]);
  const [matieres, setMatieres] = useState([]);
  const [file, setFile] = useState(null);
  const [excelHeaders, setExcelHeaders] = useState([]);
  const [previewData, setPreviewData] = useState([]);
  const [validationErrors, setValidationErrors] = useState([]);
  const [mapping, setMapping] = useState({
    matricule: "",
    nom: "",
    prenom: "",
    notes: {},
  });

  const toast = useToastHook();
  const {
    getPlannifications,
    getPlannification,
    getMatieresParClasse,
    importerNotesExcel,
  } = useAdminService();

  useEffect(() => {
    fetchPlannifications();
  }, []);

  useEffect(() => {
    if (selectedPlannification) {
      fetchPlannificationData();
    } else {
      resetData();
    }
  }, [selectedPlannification]);

  const fetchPlannifications = async () => {
    try {
      const data = await getPlannifications(slug);
      setPlannifications(data);
      if (data.length > 0) {
        setSelectedPlannification(String(data[0].id));
      }
    } catch (error) {
      toast.error("Impossible de charger les plannifications");
    } finally {
      setLoading(false);
    }
  };

  const fetchPlannificationData = async () => {
    setLoading(true);
    try {
      const data = await getPlannification(slug, selectedPlannification);
      setPlannification(data);

      const elevesConcernes = data.eleves_concernes_detail || [];
      setEleves(elevesConcernes);

      if (elevesConcernes.length > 0) {
        const premiereInscription = elevesConcernes[0]?.inscriptions?.[0];
        if (premiereInscription) {
          const classeId = premiereInscription.classe || premiereInscription.classe_id;
          await fetchMatieres(classeId);
        }
      }
    } catch (error) {
      toast.error("Impossible de charger les données");
    } finally {
      setLoading(false);
    }
  };

  const fetchMatieres = async (classeId) => {
    try {
      const data = await getMatieresParClasse(slug, { classe: classeId });
      setMatieres(data);
    } catch (error) {
      toast.error("Impossible de charger les matières");
    }
  };

  const resetData = () => {
    setPlannification(null);
    setMatieres([]);
    setEleves([]);
    setFile(null);
    setPreviewData([]);
    setValidationErrors([]);
    setExcelHeaders([]);
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    const validExtensions = [".xlsx", ".xls", ".csv"];
    const ext = selectedFile.name.substring(selectedFile.name.lastIndexOf(".")).toLowerCase();

    if (!validExtensions.includes(ext)) {
      toast.error("Veuillez sélectionner un fichier Excel (.xlsx, .xls) ou CSV");
      return;
    }

    setFile(selectedFile);
    readExcelFile(selectedFile);
  };

  const readExcelFile = (file) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(firstSheet, { header: 1, defval: "" });

        if (jsonData.length < 2) {
          toast.error("Le fichier est vide ou ne contient pas d'en-têtes");
          return;
        }

        const headers = jsonData[0].map((h, i) => h || `Colonne ${i + 1}`);
        setExcelHeaders(headers);

        const autoMapping = detectAutoMapping(headers);
        setMapping(autoMapping);

        const preview = convertToPreview(jsonData.slice(1), headers, autoMapping);
        setPreviewData(preview);
        validateData(preview);
      } catch (error) {
        toast.error("Impossible de lire le fichier Excel");
      }
    };

    reader.readAsArrayBuffer(file);
  };

  const detectAutoMapping = (headers) => {
    const mapping = {
      matricule: "",
      nom: "",
      prenom: "",
      notes: {},
    };

    headers.forEach((header, index) => {
      if (!header) return;
      const headerStr = String(header).toLowerCase().trim();

      if (headerStr.includes("matricule") || headerStr.includes("matricul") || headerStr.includes("id")) {
        mapping.matricule = index;
      } else if (headerStr.includes("nom") && !headerStr.includes("prénom") && !headerStr.includes("prenom")) {
        mapping.nom = index;
      } else if (headerStr.includes("prenom") || headerStr.includes("prénom")) {
        mapping.prenom = index;
      } else {
        matieres.forEach((matiere) => {
          const matiereLower = matiere.nom.toLowerCase();
          const matiereCodeLower = matiere.code ? matiere.code.toLowerCase() : "";
          if (headerStr.includes(matiereLower) ||
              (matiereCodeLower && headerStr.includes(matiereCodeLower)) ||
              headerStr.includes(matiere.nom.substring(0, 4).toLowerCase())) {
            mapping.notes[matiere.id] = index;
          }
        });
      }
    });

    return mapping;
  };

  const convertToPreview = (rows, headers, mapping) => {
    return rows.map((row, rowIndex) => {
      const previewRow = {
        matricule: mapping.matricule !== "" && row[mapping.matricule] !== undefined ? String(row[mapping.matricule]).trim() : "",
        nom: mapping.nom !== "" && row[mapping.nom] !== undefined ? String(row[mapping.nom]).trim() : "",
        prenom: mapping.prenom !== "" && row[mapping.prenom] !== undefined ? String(row[mapping.prenom]).trim() : "",
        notes: {},
        rowIndex: rowIndex + 2,
      };

      Object.keys(mapping.notes).forEach((matiereId) => {
        const colIndex = mapping.notes[matiereId];
        previewRow.notes[matiereId] = colIndex !== "" && row[colIndex] !== undefined ? String(row[colIndex]).trim() : "";
      });

      return previewRow;
    });
  };

  const validateData = (data) => {
    const errors = [];
    const rowsToValidate = data.length > 1000 ? 1000 : data.length;

    for (let i = 0; i < rowsToValidate; i++) {
      const row = data[i];

      if (!row.matricule && !row.nom) {
        errors.push(`Ligne ${row.rowIndex}: Matricule ou Nom requis`);
      }

      Object.keys(row.notes).forEach((matiereId) => {
        const noteValue = row.notes[matiereId];
        if (noteValue !== "" && noteValue !== null && noteValue !== undefined) {
          const noteNum = parseFloat(noteValue);
          if (isNaN(noteNum)) {
            errors.push(`Ligne ${row.rowIndex}: Note invalide "${noteValue}"`);
          } else if (noteNum < 0 || noteNum > 20) {
            errors.push(`Ligne ${row.rowIndex}: Note ${noteNum} hors limites (0-20)`);
          }
        }
      });

      if (errors.length > 100) {
        errors.push(`... et ${data.length - i - 1} lignes non vérifiées`);
        break;
      }
    }

    setValidationErrors(errors);
    return errors.length === 0;
  };

  const downloadTemplate = () => {
    if (!matieres.length) {
      toast.warning("Veuillez d'abord sélectionner une plannification");
      return;
    }

    const headers = ["Matricule", "Nom", "Prénom", ...matieres.map((m) => `${m.nom} (Coef: ${m.coefficient})`)];
    const data = [
      headers,
      ...eleves.map((eleve) => [
        eleve.matricule || "",
        eleve.nom || "",
        eleve.prenom || "",
        ...matieres.map(() => ""),
      ]),
    ];

    const ws = XLSX.utils.aoa_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Template");

    const wscols = [
      { wch: 15 },
      { wch: 20 },
      { wch: 20 },
      ...matieres.map(() => ({ wch: 20 })),
    ];
    ws["!cols"] = wscols;

    XLSX.writeFile(wb, `Template_Notes_${plannification?.nom || "Plannification"}.xlsx`);
    toast.success("Template téléchargé avec succès");
  };

  const handleImport = async () => {
    if (!file) {
      toast.error("Veuillez sélectionner un fichier Excel");
      return;
    }

    if (!validateData(previewData)) {
      toast.error("Des erreurs de validation doivent être corrigées");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("periode", selectedPlannification);

      const result = await importerNotesExcel(slug, formData);
      
      let message = `Import terminé : ${result.created || 0} notes créées`;
      if (result.updated) message += `, ${result.updated} mises à jour`;
      if (result.deleted) message += `, ${result.deleted} supprimées`;
      toast.success(message);

      // Rediriger vers la page de saisie des notes
      navigate(`/${slug}/admin/notes?plannification=${selectedPlannification}`);
    } catch (error) {
      toast.error(error.response?.data?.error || "Erreur lors de l'import");
    } finally {
      setUploading(false);
    }
  };

  const resetImport = () => {
    setFile(null);
    setPreviewData([]);
    setValidationErrors([]);
    setExcelHeaders([]);
  };

  if (loading) return <Loader />;

  return (
    <div>
      {/* En-tête avec bouton retour */}
      <div className="sb2" style={{ justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button
            className="bsec"
            onClick={() => navigate(`/${slug}/admin/notes`)}
          >
            <i className="ti ti-arrow-left" /> Retour
          </button>
          <h2 style={{ margin: 0, color: "var(--pr)" }}>
            <i className="ti ti-file-spreadsheet" style={{ marginRight: 8 }} />
            Import des notes depuis Excel
          </h2>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="bsec" onClick={downloadTemplate} disabled={!matieres.length}>
            <i className="ti ti-download" /> Télécharger le template
          </button>
        </div>
      </div>

      {/* Sélecteur de plannification */}
      <Card title="Sélectionnez une plannification" icon="ti-calendar">
        <select
          className="planif-select"
          value={selectedPlannification}
          onChange={(e) => setSelectedPlannification(e.target.value)}
        >
          <option value="">-- Sélectionner une plannification --</option>
          {plannifications.map((p) => (
            <option key={p.id} value={p.id}>
              {p.nom} — {p.trimestre_detail?.nom || "Sans trimestre"}
            </option>
          ))}
        </select>
      </Card>

      {/* Informations sur la plannification */}
      {selectedPlannification && plannification && (
        <Card title={`${plannification.nom}`} icon="ti-info-circle">
          <div style={{ display: "flex", gap: 24, flexWrap: "wrap", fontSize: 14 }}>
            <span><strong>Trimestre :</strong> {plannification.trimestre_detail?.nom || "—"}</span>
            <span><strong>Élèves :</strong> {eleves.length}</span>
            <span><strong>Matières :</strong> {matieres.length}</span>
          </div>
        </Card>
      )}

      {/* Zone d'upload */}
      {selectedPlannification && (
        <Card title="Importer le fichier Excel" icon="ti-upload">
          <div
            className="upload-area"
            onDragOver={(e) => {
              e.preventDefault();
              e.currentTarget.style.borderColor = "var(--pr)";
              e.currentTarget.style.background = "var(--pr-bg)";
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              e.currentTarget.style.borderColor = "var(--b)";
              e.currentTarget.style.background = "transparent";
            }}
            onDrop={(e) => {
              e.preventDefault();
              e.currentTarget.style.borderColor = "var(--b)";
              e.currentTarget.style.background = "transparent";
              if (e.dataTransfer.files.length) {
                const droppedFile = e.dataTransfer.files[0];
                setFile(droppedFile);
                readExcelFile(droppedFile);
              }
            }}
          >
            <div style={{ fontSize: 48, color: "var(--pr)", marginBottom: 12 }}>
              <i className="ti ti-file-spreadsheet" />
            </div>
            <h4 style={{ color: "var(--t)", marginBottom: 8 }}>
              {file ? file.name : "Déposer votre fichier Excel ici"}
            </h4>
            <p style={{ color: "var(--m)", marginBottom: 16, fontSize: 14 }}>
              ou cliquez pour sélectionner un fichier (.xlsx, .xls, .csv)
            </p>
            <input
              id="file-upload"
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={handleFileChange}
              style={{ display: "none" }}
            />
            <label htmlFor="file-upload" className="bprim" style={{ cursor: "pointer" }}>
              <i className="ti ti-folder-open" /> Parcourir
            </label>
            {file && (
              <div style={{ marginTop: 12, fontSize: 13, color: "var(--m)" }}>
                <i className="ti ti-file" /> {file.name} ({(file.size / 1024).toFixed(0)} Ko)
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Mapping et Aperçu */}
      {file && previewData.length > 0 && (
        <>
          <Card title="Correspondance des colonnes" icon="ti-list">
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 }}>
              {excelHeaders.map((header, index) => (
                <span key={index} className="pill neu" style={{ fontSize: 12 }}>
                  {index + 1}. {header}
                </span>
              ))}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, fontSize: 14 }}>
              <div><span style={{ color: "var(--m)" }}>Matricule :</span> {mapping.matricule !== "" ? `Colonne ${parseInt(mapping.matricule) + 1}` : "Non mappé"}</div>
              <div><span style={{ color: "var(--m)" }}>Nom :</span> {mapping.nom !== "" ? `Colonne ${parseInt(mapping.nom) + 1}` : "Non mappé"}</div>
              <div><span style={{ color: "var(--m)" }}>Prénom :</span> {mapping.prenom !== "" ? `Colonne ${parseInt(mapping.prenom) + 1}` : "Non mappé"}</div>
              <div><span style={{ color: "var(--m)" }}>Notes :</span> {Object.keys(mapping.notes).length} matière(s) mappée(s)</div>
            </div>
          </Card>

          {/* Erreurs de validation */}
          {validationErrors.length > 0 && (
            <div className="alert-banner" style={{ borderColor: "var(--rd)", background: "var(--rd-bg)", color: "var(--rd)" }}>
              <i className="ti ti-alert-circle" />
              <span>
                <strong>{validationErrors.length} erreur(s) de validation :</strong>
                <div style={{ maxHeight: 150, overflowY: "auto", marginTop: 6, fontSize: 13 }}>
                  {validationErrors.slice(0, 30).map((err, i) => (
                    <div key={i}>• {err}</div>
                  ))}
                  {validationErrors.length > 30 && (
                    <div>... et {validationErrors.length - 30} autres erreurs</div>
                  )}
                </div>
              </span>
            </div>
          )}

          {/* Aperçu des données */}
          <Card title={`Aperçu des données (${previewData.length} lignes)`} icon="ti-eye">
            <div style={{ overflowX: "auto", maxHeight: 500, overflowY: "auto" }}>
              <table className="tbl">
                <thead>
                  <tr style={{ position: "sticky", top: 0, background: "var(--s)", zIndex: 10 }}>
                    <th>#</th>
                    <th>Matricule</th>
                    <th>Nom</th>
                    <th>Prénom</th>
                    {matieres.map((m) => (
                      <th key={m.id} style={{ textAlign: "center" }}>
                        <div>{m.nom}</div>
                        <div style={{ fontSize: 11, color: "var(--m)" }}>Coef: {m.coefficient}</div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {previewData.slice(0, 50).map((row, i) => {
                    return (
                      <tr key={i}>
                        <td style={{ color: "var(--m)", fontSize: 12 }}>{row.rowIndex}</td>
                        <td style={{ color: row.matricule ? "var(--t)" : "var(--rd)" }}>
                          {row.matricule || "—"}
                        </td>
                        <td style={{ color: row.nom ? "var(--t)" : "var(--rd)" }}>
                          {row.nom || "—"}
                        </td>
                        <td style={{ color: row.prenom ? "var(--t)" : "var(--rd)" }}>
                          {row.prenom || "—"}
                        </td>
                        {matieres.map((m) => {
                          const noteValue = row.notes[m.id];
                          const isValid = noteValue === "" || (!isNaN(parseFloat(noteValue)) && parseFloat(noteValue) >= 0 && parseFloat(noteValue) <= 20);
                          return (
                            <td key={m.id} style={{ textAlign: "center" }}>
                              {noteValue || "—"}
                              {noteValue && (
                                <div style={{ fontSize: 10, color: isValid ? "var(--gn)" : "var(--rd)", marginTop: 2 }}>
                                  {isValid ? <i className="ti ti-check" /> : <i className="ti ti-x" />}
                                  {isValid ? " Valide" : " Invalide"}
                                </div>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                  {previewData.length > 50 && (
                    <tr><td colSpan={matieres.length + 4} style={{ textAlign: "center", color: "var(--h)", padding: 12 }}>
                      ... et {previewData.length - 50} autres lignes
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>

          {/* Boutons d'action */}
          <div className="factions" style={{ justifyContent: "center", gap: 16 }}>
            <button className="bsec" onClick={resetImport}>
              <i className="ti ti-refresh" /> Réinitialiser
            </button>
            <button
              className="bprim"
              onClick={handleImport}
              disabled={uploading || validationErrors.length > 0}
            >
              {uploading ? (
                <><i className="ti ti-loader" style={{ animation: "spin 1s linear infinite" }} /> Import en cours...</>
              ) : (
                <><i className="ti ti-upload" /> Importer les notes</>
              )}
            </button>
          </div>
        </>
      )}

      {/* Instructions */}
      <Card title="Instructions" icon="ti-info-circle">
        <ol style={{ color: "var(--m)", lineHeight: 1.8, margin: 0, paddingLeft: 20 }}>
          <li>Sélectionnez une plannification</li>
          <li>Téléchargez le template Excel pour voir le format attendu</li>
          <li>Remplissez le template avec les notes des élèves</li>
          <li>Importez votre fichier Excel</li>
          <li>Vérifiez la correspondance des colonnes</li>
          <li>Consultez l'aperçu et corrigez les erreurs éventuelles</li>
          <li>Cliquez sur "Importer les notes"</li>
        </ol>
        <div style={{
          marginTop: 12,
          padding: 12,
          background: "var(--gn-bg)",
          borderRadius: 6,
          fontSize: 13,
          color: "var(--gn)"
        }}>
          <i className="ti ti-info-circle" /> Les notes seront importées pour la plannification <strong>{plannification?.nom}</strong>.
          Les cellules vides ne modifieront pas les notes existantes.
        </div>
      </Card>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .upload-area {
          border: 2px dashed var(--b);
          border-radius: 10px;
          padding: 40px 20px;
          text-align: center;
          transition: all 0.3s;
          cursor: pointer;
        }

        .upload-area:hover {
          border-color: var(--pr);
          background: var(--pr-bg);
        }

        .planif-select {
          padding: 8px 16px;
          border: 0.5px solid var(--b2);
          border-radius: 6px;
          font-size: 15px;
          background: var(--s);
          color: var(--t);
          font-family: inherit;
          min-width: 300px;
        }

        .alert-banner {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 18px;
          background: var(--am-bg);
          border: 2px solid var(--am);
          border-radius: 8px;
          color: var(--am);
          font-size: 14px;
          margin-bottom: 14px;
          flex-wrap: wrap;
        }

        .alert-banner i { font-size: 22px; flex-shrink: 0; }
        .alert-banner span { flex: 1; }

        .tbl td {
          padding: 6px 8px;
          font-size: 13px;
        }

        .tbl th {
          padding: 6px 8px;
          font-size: 13px;
        }

        @media (max-width: 768px) {
          .planif-select { min-width: 200px; }
        }
      `}</style>
    </div>
  );
}